import type { OutreachEntryEvent, OutreachMessage } from '@discovr/types';
import { CLOSE_REASON_LABELS } from './sendLabels';

export type ThreadItem = { type: 'message'; message: OutreachMessage } | { type: 'event'; event: OutreachEntryEvent };

/** Reasons only the system writes on a close (Go: bounced and unsubscribed are system-only). */
const SYSTEM_CLOSE_REASONS: Record<string, string> = { unsubscribed: 'Unsubscribed', bounced: 'Bounced' };

/** Who acted: the name the backend sends, else (older backend) the actor itself, with 'system' and 'agent' in plain words. */
function actorLabel(event: OutreachEntryEvent): string {
  if (event.actorName) return event.actorName;
  if (event.actor === 'system') return 'System';
  if (event.actor === 'agent') return 'AI';
  return event.actor;
}

/** The email shown on hover, for an admin whose name is displayed. System and agent actors have none. */
export function timelineActorTitle(event: OutreachEntryEvent): string | undefined {
  return event.actorKind === 'admin' && event.actorName && event.actorName !== event.actor ? event.actor : undefined;
}

const EVENT_VERBS: Record<OutreachEntryEvent['kind'], string> = {
  rate_agreed: 'agreed the rate',
  closed: 'closed the conversation',
  reopened: 'reopened the conversation',
  removed: 'removed the creator',
  reinstated: 'reinstated the creator',
  followups_paused: 'paused follow-ups',
  followups_resumed: 'resumed follow-ups',
  sent_to_brand: 'sent the creator to the brand',
};

/** One compact timeline line: "Tech Admin closed the conversation · Not interested". */
export function timelineLabel(event: OutreachEntryEvent): string {
  const base = `${actorLabel(event)} ${EVENT_VERBS[event.kind] ?? event.kind.replace(/_/g, ' ')}`;
  if (event.kind !== 'closed' || !event.reason) return base;
  const reason = SYSTEM_CLOSE_REASONS[event.reason] ?? (CLOSE_REASON_LABELS as Record<string, string | undefined>)[event.reason];
  return reason ? `${base} · ${reason}` : base;
}

/** The messages in their order with each event placed before the first message that came after it. An event after
 *  every message goes last; a message whose time cannot be read keeps its place and never moves an event. */
export function mergeThreadItems(messages: OutreachMessage[], events: OutreachEntryEvent[], timeOf: (m: OutreachMessage) => string | undefined): ThreadItem[] {
  const pending = [...events].sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.id - b.id);
  const items: ThreadItem[] = [];
  for (const message of messages) {
    const at = Date.parse(timeOf(message) ?? '');
    for (let event = pending[0]; event && !Number.isNaN(at) && Date.parse(event.at) < at; event = pending[0]) {
      items.push({ type: 'event', event: pending.shift() ?? event });
    }
    items.push({ type: 'message', message });
  }
  for (const event of pending) items.push({ type: 'event', event });
  return items;
}
